import React from 'react';
import { Package } from 'lucide-react';
import { Icon } from '@grofers/dock-design-kit';
import { AddItemButtonCustom } from '../../../../UiKit/addItemButtonCustom/addItemButtonCustom';
import Button from '../../../ComponentsV1/Button';
import GrievanceDropdown from './GrievanceDropdown';

/**
 * Product row reused across the Copilot flow wherever a product needs to
 * show its image/name/qty with a stepper - the "Customer's Selection"
 * cards in step 1, and the per-complaint product rows in step 2/3.
 * `onRemove` is optional: pass it to show a Remove action (step 1/2), omit
 * it for a read-only row (e.g. step 3's product condition card).
 *
 * `grievanceOptions` is step 3-only: `confirm_complaint`'s `selected_items`
 * now carries each item's own `grievance_level` choices, shown as a small
 * dropdown under the name/qty (Figma node 2328:65422) - omitted entirely
 * when the item has none (step 1/2 never pass this). Uses the portal-based
 * `GrievanceDropdown` rather than dock's own `Dropdown` - this card sits
 * inside wrappers (the card itself, the accordion step) that clip overflow
 * to keep their rounded corners, which cuts an inline-rendered dropdown
 * panel off (same reason ItemPickerDropdown/ComplaintTypeDropdown use a
 * portal).
 */
const ProductCard = ({
  imageUrl = null,
  name = '',
  quantityLabel = '',
  count = 1,
  maxCount = Infinity,
  onIncrement = () => {},
  onDecrement = () => {},
  increaseDisabled = null,
  decreaseDisabled = null,
  onRemove = null,
  disabled = false,
  grievanceOptions = [],
  selectedGrievance = null,
  onGrievanceChange = () => {},
}) => {
  return (
    <div className='tw-w-full tw-bg-white tw-p-[6px] tw-flex tw-gap-2 tw-items-start'>
      <div className='tw-size-[84px] tw-rounded-lg tw-bg-[#f3f4f6] tw-shrink-0 tw-flex tw-items-center tw-justify-center tw-overflow-hidden'>
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={name}
            className='tw-size-full tw-object-cover'
          />
        ) : (
          <Package size={28} className='tw-text-[#9da4ae]' />
        )}
      </div>
      <div className='tw-flex tw-flex-1 tw-min-w-0 tw-flex-col tw-gap-[8px] tw-self-stretch'>
        <div className='tw-flex tw-items-center tw-justify-between tw-w-full'>
          <div className='tw-flex tw-flex-col tw-gap-[4px] tw-min-w-0 tw-max-w-[197px] tw-mr-2'>
            <p className='tw-text-[11px] tw-font-medium tw-text-grey-900 tw-m-0 tw-leading-[14px]'>
              {name}
            </p>
            <p className='tw-text-[10px] tw-text-[#384250] tw-m-0 tw-leading-[14px]'>
              {quantityLabel}
            </p>
          </div>
          <div className='tw-flex tw-flex-col tw-items-end tw-gap-2'>
            <AddItemButtonCustom
              count={count}
              handleIncrease={onIncrement}
              handleDecrease={onDecrement}
              increaseDisabled={
                increaseDisabled ?? (disabled || count >= maxCount)
              }
              decreaseDisabled={decreaseDisabled ?? disabled}
              disabled={disabled}
              isCopilotView={true}
            />
            {onRemove && (
              <>
                <Button
                  onClick={onRemove}
                  size='small'
                  type='outline'
                  className={'!tw-border-red-500'}
                  title={
                    <span className='tw-flex tw-w-28 tw-h-7 tw-items-center tw-gap-1 tw-text-red-600 tw-text-[12px]'>
                      <Icon
                        iconColor={{ color: 'red', weight: '600' }}
                        name='trash'
                        size='xs'
                      />
                      Remove
                    </span>
                  }
                />
              </>
            )}
          </div>
        </div>

        {grievanceOptions.length > 0 && (
          <GrievanceDropdown
            options={grievanceOptions}
            value={selectedGrievance}
            onChange={onGrievanceChange}
          />
        )}
      </div>
    </div>
  );
};

export default ProductCard;
